import { useContext, useEffect, useReducer, useState } from 'react';
import { AppState } from '../context/general';
import type { OfflineMode } from '../connector/feed-engine';

// Live offline state of the current feed engine. 'none' while online; the anonymous engine
// never reports anything else (it has no server session to lose).
export function useOfflineMode(): OfflineMode {
  const { feedEngine } = useContext(AppState);
  const [mode, setMode] = useState<OfflineMode>(() => feedEngine.getOfflineMode());
  useEffect(() => {
    // Re-read on engine swap (login/logout replaces the instance): the callback only fires
    // on changes, so a mode set before we subscribed would never reach this component.
    setMode(feedEngine.getOfflineMode());
    return feedEngine.onOfflineChange(setMode);
  }, [feedEngine]);
  return mode;
}

// Whether the app can be used without the server right now (offline reading on and a library
// loaded). Re-renders when the engine's state or offline mode changes.
export function useCanReadOffline(): boolean {
  const { feedEngine } = useContext(AppState);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const unsubscribeState = feedEngine.onStateChange(rerender);
    const unsubscribeOffline = feedEngine.onOfflineChange(rerender);
    return () => { unsubscribeState(); unsubscribeOffline(); };
  }, [feedEngine]);
  return feedEngine.canReadOffline();
}

// True while offline and the app runs on the data stored for offline reading.
export function useReadingOffline(): boolean {
  const offlineMode = useOfflineMode();
  const canReadOffline = useCanReadOffline();
  return offlineMode != 'none' && canReadOffline;
}
